// PartnersPanel — GM Console tab for curating HUB partner / collab cards.
// Every row (name, title, work_name, description, image, link, order,
// active) is fully editable and immediately visible on the Hub via
// /api/gm/partners. Ordering follows the `order` field (lower = first).
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Handshake, Upload, Eye, EyeOff } from "lucide-react";

function emptyPartner() {
  return {
    id: null,
    name: "",
    title: "",
    work_name: "",
    description: "",
    image: "",
    link: "",
    active: true,
    order: 100,
  };
}

export default function PartnersPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    setLoading(true);
    api.gmListPartners()
      .then(r => setRows(r.partners || []))
      .catch(() => toast.error("Impossibile caricare i partner"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async (draft) => {
    try {
      await api.gmUpsertPartner(draft);
      toast.success(draft.id ? "Partner aggiornato" : "Partner creato");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio fallito");
    }
  };

  const del = async (id) => {
    try {
      await api.gmDeletePartner(id);
      toast.success("Partner rimosso");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eliminazione fallita");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between border-b border-[#262626] pb-2">
        <div className="flex items-center gap-2">
          <Handshake className="w-4 h-4 text-[#E5C34A]" strokeWidth={1.5} />
          <div className="font-heading text-sm tracking-[0.3em] uppercase text-[#E5C34A]">Partner &amp; Collab</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#8c8c8c]">
            {loading ? "..." : `${rows.length} ${rows.length === 1 ? "voce" : "voci"}`}
          </div>
        </div>
        <button data-testid="partner-new-btn" onClick={() => setEditing(emptyPartner())}
          className="px-3 py-2 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase hover:bg-[#4A5D23]/10 flex items-center gap-1.5">
          <Plus className="w-3 h-3" /> Nuovo Partner
        </button>
      </div>

      {rows.length === 0 && !loading && (
        <div className="panel p-4 text-center text-xs italic text-[#666]">
          Nessun partner ancora. Crea la prima collaborazione con "Nuovo Partner".
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map(p => (
          <div key={p.id} data-testid={`partner-card-${p.id}`}
            className={`panel p-3 space-y-2 ${p.active ? "" : "opacity-60"}`}>
            <div className="w-full h-32 bg-[#0a0a0a] border border-[#262626] overflow-hidden">
              {p.image ? (
                <img src={p.image} alt={p.name} className="w-full h-full object-cover"
                  onError={e => { e.currentTarget.style.opacity = 0.2; }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center font-mono text-[9px] uppercase tracking-widest text-[#333]">
                  senza immagine
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                {p.active ? <Eye className="w-3 h-3 text-[#6E8B3D]" /> : <EyeOff className="w-3 h-3 text-[#666]" />}
                <div className="font-heading text-sm tracking-widest uppercase text-[#E5C34A] truncate">{p.name}</div>
              </div>
              {p.title && <div className="font-mono text-[9px] uppercase tracking-widest text-[#B89C3A]">{p.title}</div>}
              {p.work_name && <div className="font-mono text-[10px] text-[#8FD1E5]">{p.work_name}</div>}
              {p.description && (
                <div className="text-[10px] text-[#B0ADA5] mt-1 line-clamp-3">{p.description}</div>
              )}
              <div className="font-mono text-[9px] text-[#666] mt-1">Order: {p.order}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(p)} data-testid={`partner-edit-${p.id}`}
                className="font-mono text-[9px] uppercase tracking-widest text-[#B89C3A] hover:text-[#E5C34A]">Modifica</button>
              <button onClick={() => setDeleting(p)} data-testid={`partner-del-${p.id}`}
                className="font-mono text-[9px] uppercase tracking-widest text-[#E33333] ml-auto hover:text-[#8A0303]">Elimina</button>
            </div>
          </div>
        ))}
      </div>

      {editing && <PartnerEditor initial={editing} onCancel={() => setEditing(null)} onSave={save} />}
      {deleting && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setDeleting(null)}>
          <div className="panel p-4 w-full max-w-md space-y-3" onClick={e => e.stopPropagation()}>
            <div className="font-heading text-sm tracking-widest uppercase text-[#E33333]">Elimina partner</div>
            <div className="text-xs text-[#B0ADA5]">Vuoi davvero rimuovere <b>{deleting.name}</b>? Sarà tolto anche dall'Hub.</div>
            <div className="flex justify-end gap-2 pt-2 border-t border-[#262626]">
              <button onClick={() => setDeleting(null)} className="px-3 py-2 border border-[#262626] font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c]">Annulla</button>
              <button data-testid="partner-del-confirm" onClick={() => del(deleting.id)} className="px-3 py-2 border border-[#E33333] text-[#E33333] font-heading text-[10px] tracking-widest uppercase hover:bg-[#8A0303]/10">Elimina</button>
            </div>
          </div>
        </div>
      )}

      <div className="font-mono text-[9px] text-[#666] pt-2 border-t border-[#262626]">
        Le voci con Attivo=Sì compaiono automaticamente nel pannello "Partner &amp; Collab" del Hub.
      </div>
    </div>
  );
}

function PartnerEditor({ initial, onCancel, onSave }) {
  const [p, set] = useState({ ...emptyPartner(), ...initial });
  const u = (k, v) => set({ ...p, [k]: v });

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Max 4MB"); return; }
    try {
      const r = await api.gmUploadImage(f);
      u("image", r.url);
      toast.success("Immagine caricata");
    } catch {
      const reader = new FileReader();
      reader.onload = () => u("image", reader.result);
      reader.readAsDataURL(f);
      toast.message("Salvata inline (verrà convertita al salvataggio)");
    }
  };

  const submit = () => {
    if (!p.name?.trim()) { toast.error("Il nome del partner è obbligatorio"); return; }
    onSave({ ...p, order: Number(p.order) || 100 });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onCancel}>
      <div className="panel p-4 w-full max-w-2xl space-y-3 my-4" onClick={e => e.stopPropagation()} data-testid="partner-editor">
        <div className="flex items-center justify-between border-b border-[#262626] pb-2">
          <div className="font-heading text-sm tracking-widest uppercase text-[#E5C34A]">
            {initial.id ? "Modifica Partner" : "Nuovo Partner"}
          </div>
          <button onClick={onCancel} className="text-[#666] hover:text-[#E2DFD8]"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Nome (studio / artista)" required>
            <input data-testid="partner-name" value={p.name} onChange={e => u("name", e.target.value)}
              placeholder="Es. Ravencross Studio"
              className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8] font-mono focus:border-[#E5C34A] outline-none" />
          </Field>
          <Field label="Titolo / Ruolo">
            <input data-testid="partner-title" value={p.title} onChange={e => u("title", e.target.value)}
              placeholder="Es. Illustratore ufficiale"
              className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8] font-mono focus:border-[#E5C34A] outline-none" />
          </Field>
          <Field label="Nome dell'opera / collab">
            <input data-testid="partner-work" value={p.work_name} onChange={e => u("work_name", e.target.value)}
              placeholder="Es. The Ravencross Prints"
              className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8] font-mono focus:border-[#E5C34A] outline-none" />
          </Field>
          <Field label="Link opera (opzionale)">
            <input data-testid="partner-link" value={p.link} onChange={e => u("link", e.target.value)}
              placeholder="https://..."
              className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8] font-mono focus:border-[#E5C34A] outline-none" />
          </Field>
        </div>

        <Field label="Descrizione">
          <textarea data-testid="partner-desc" rows={3} value={p.description} onChange={e => u("description", e.target.value)}
            placeholder="Racconta la collaborazione..."
            className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8] font-mono focus:border-[#E5C34A] outline-none" />
        </Field>

        <Field label="Immagine (URL o upload)">
          <div className="space-y-1">
            <input data-testid="partner-image-url" value={p.image || ""} onChange={e => u("image", e.target.value)}
              placeholder="https://... oppure carica un file"
              className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8] font-mono focus:border-[#E5C34A] outline-none" />
            <label className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#B89C3A] hover:text-[#E5C34A] cursor-pointer">
              <Upload className="w-3 h-3" /> upload immagine
              <input data-testid="partner-image-file" type="file" accept="image/*" onChange={onFile} className="hidden" />
            </label>
            {p.image && (
              <div className="w-full h-32 bg-[#0a0a0a] border border-[#262626] mt-1 overflow-hidden">
                <img src={p.image} alt="preview" className="w-full h-full object-cover" onError={e => { e.currentTarget.style.opacity = 0.2; }} />
              </div>
            )}
          </div>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Ordine (numero più basso = più in alto nella galleria)">
            <input data-testid="partner-order" type="number" value={p.order} onChange={e => u("order", e.target.value)}
              className="w-full bg-[#0a0a0a] border border-[#333] px-2 py-1.5 text-xs text-[#E2DFD8] font-mono focus:border-[#E5C34A] outline-none" />
          </Field>
          <Field label="Attivo (visibile nell'Hub)">
            <label className="flex items-center gap-2 py-1.5 cursor-pointer">
              <input type="checkbox" data-testid="partner-active" checked={!!p.active}
                onChange={e => u("active", e.target.checked)} className="accent-[#E55A00]" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#B0ADA5]">
                {p.active ? "Visibile" : "Nascosto (bozza)"}
              </span>
            </label>
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-[#262626]">
          <button onClick={onCancel} className="px-3 py-2 border border-[#262626] font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c]">Annulla</button>
          <button data-testid="partner-save" onClick={submit}
            className="px-3 py-2 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase hover:bg-[#4A5D23]/10 flex items-center gap-1.5">
            <Save className="w-3 h-3" /> Salva
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c] mb-1">
        {label} {required && <span className="text-[#E33333]">*</span>}
      </div>
      {children}
    </label>
  );
}
